/**
 * TaxInvoicePage
 * 세금계산서 관리 페이지
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTaxInvoices } from '../../use-cases/useTaxInvoices';
import { TaxInvoice, formatKRW, getDirectionLabel, getDirectionColor } from '../../models/Tax';
import { InvoiceFormModal } from './InvoiceFormModal';
import { CsvUploadModal } from './CsvUploadModal';

export const TaxInvoicePage: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [direction, setDirection] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [editItem, setEditItem] = useState<TaxInvoice | null>(null);

  const {
    invoices,
    loading,
    error,
    summary,
    fetchInvoices,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    uploadCsv,
    exportCsv,
  } = useTaxInvoices();

  const loadData = useCallback(() => {
    fetchInvoices({ year, month, direction: direction || undefined, search: search || undefined });
  }, [fetchInvoices, year, month, direction, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('이 세금계산서를 삭제하시겠습니까?')) return;
    const ok = await deleteInvoice(id);
    if (ok) loadData();
  };

  const handleEdit = (item: TaxInvoice) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleSubmit = async (data: any) => {
    if (editItem) {
      const ok = await updateInvoice(editItem.id, data);
      if (ok) loadData();
      return ok;
    } else {
      const ok = await createInvoice(data);
      if (ok) loadData();
      return ok;
    }
  };

  const yearOptions = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">세금계산서 관리</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCsv(true)}
            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            CSV 업로드
          </button>
          <button
            onClick={() => exportCsv(year, month, direction || undefined)}
            className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100"
          >
            CSV 내보내기
          </button>
          <button
            onClick={() => { setEditItem(null); setShowForm(true); }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            + 추가
          </button>
        </div>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">총 건수</p>
          <p className="text-2xl font-bold text-gray-900">{summary.count}건</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">매출 세액</p>
          <p className="text-2xl font-bold text-green-600">{formatKRW(summary.salesTax)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">매입 세액</p>
          <p className="text-2xl font-bold text-orange-600">{formatKRW(summary.purchaseTax)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">차이 (매출-매입)</p>
          <p className={`text-2xl font-bold ${summary.salesTax - summary.purchaseTax >= 0 ? 'text-red-600' : 'text-blue-600'}`}>
            {formatKRW(summary.salesTax - summary.purchaseTax)}
          </p>
        </div>
      </div>

      {/* 필터 */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
            {yearOptions.map((y) => <option key={y} value={y}>{y}년</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}월</option>)}
          </select>
          <select value={direction} onChange={(e) => setDirection(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
            <option value="">전체</option>
            <option value="sales">매출</option>
            <option value="purchase">매입</option>
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="거래처/사업자번호 검색..."
            className="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1 min-w-[200px]"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* 테이블 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">구분</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">거래처</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">사업자번호</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">공급가액</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">세액</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">합계</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">작업</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">불러오는 중...</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">데이터가 없습니다.</td></tr>
              ) : (
                invoices.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getDirectionColor(item.direction)}`}>
                        {getDirectionLabel(item.direction)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{item.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {item.counterpart_name}
                      {item.memo && <div className="text-xs text-gray-400 truncate max-w-[200px]">{item.memo}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.business_number || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">{item.supply_amount.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-blue-600 text-right whitespace-nowrap">{item.tax_amount.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right whitespace-nowrap">{item.total.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => handleEdit(item)} className="text-xs text-blue-600 hover:text-blue-800 mr-2">수정</button>
                      <button onClick={() => handleDelete(item.id)} className="text-xs text-red-600 hover:text-red-800">삭제</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <InvoiceFormModal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditItem(null); }}
        onSubmit={handleSubmit}
        editItem={editItem}
      />

      <CsvUploadModal
        isOpen={showCsv}
        onClose={() => setShowCsv(false)}
        onUpload={uploadCsv}
        onRefresh={loadData}
      />
    </div>
  );
};
